import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { ApiResponse } from '@/lib/api/client'
import type {
    ShopContactInput,
    ShopHomeKind,
    ShopHomeSection,
    ShopHomeSectionInput,
} from '@/lib/api/shop-content-api'
import { shopContentApi } from '@/lib/api/shop-content-api'
import { m } from '@/lib/paraglide/messages'

export const shopContentKeys = {
    home: ['shop-home'] as const,
    contact: ['shop-contact'] as const,
}

export function useShopHome() {
    return useQuery({
        queryKey: shopContentKeys.home,
        queryFn: () => shopContentApi.home(),
    })
}

// Lỗi kiểm dữ liệu (SHOP_FIELD_INVALID, SHOP_CONCURRENT_CHANGE) do màn hiện cạnh
// ô nhập; hook chỉ báo thành công và thay khối vừa lưu (version mới) vào cache
// để lần lưu sau gửi đúng version.
export function useSaveShopHomeSection() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            kind,
            input,
        }: {
            kind: ShopHomeKind
            input: ShopHomeSectionInput
        }) => shopContentApi.saveHome(kind, input),
        onSuccess: (res) => {
            toast.success(m.shop_home_saved())
            const saved = res.data
            if (!saved) return
            qc.setQueryData<ApiResponse<{ sections: ShopHomeSection[] }>>(
                shopContentKeys.home,
                (old) =>
                    old?.data
                        ? {
                              ...old,
                              data: {
                                  sections: old.data.sections.map((s) =>
                                      s.kind === saved.kind ? saved : s,
                                  ),
                              },
                          }
                        : old,
            )
        },
    })
}

export function useShopContact() {
    return useQuery({
        queryKey: shopContentKeys.contact,
        queryFn: () => shopContentApi.contact(),
    })
}

export function useSaveShopContact() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (input: ShopContactInput) =>
            shopContentApi.saveContact(input),
        onSuccess: (res) => {
            toast.success(m.shop_contact_saved())
            if (res.data) qc.setQueryData(shopContentKeys.contact, res)
        },
    })
}
